"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import Icon from "@/components/ui/AppIcon";
import { ApiClientError, apiFetch } from "@/lib/api/client";
import KPIBentoGrid from "./components/KPIBentoGrid";
import CaseDistributionChart from "./components/CaseDistributionChart";
import TaskCompletionChart from "./components/TaskCompletionChart";
import UrgentAlertsPanel from "./components/UrgentAlertsPanel";
import RecentActivityFeed from "./components/RecentActivityFeed";
import UpcomingDeadlinesTable from "./components/UpcomingDeadlinesTable";
import type { DashboardSummary } from "./types";
import { useAuth } from "@/contexts/AuthContext";
import { useRouter } from "next/navigation";

export default function DashboardPage() {
  const router = useRouter();
  const { user, hasPermission } = useAuth();
  const isClientUser = user?.roles?.includes("client") || user?.roles?.includes("client_user");
  const [headerData, setHeaderData] = useState<DashboardSummary['header'] | null>(null);
  const [kpiData, setKpiData] = useState<DashboardSummary['kpis'] | null>(null);
  const [alertsData, setAlertsData] = useState<DashboardSummary['urgentAlerts'] | null>(null);
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const redirectedRef = useRef(false);

  const getEndOfMonth = (monthsAgo: number) => {
    const d = new Date();
    d.setMonth(d.getMonth() - monthsAgo + 1);
    d.setDate(0);
    return d.toISOString().split('T')[0];
  };

  useEffect(() => {
    let active = true;

    const loadTopLevelData = async () => {
      setLoadingHeader(true);
      try {
        const queryParams = selectedDate ? `?date=${selectedDate}` : "";
        const [kpiRes, alertsRes] = await Promise.all([
          apiFetch<{ generatedAt: string; header: DashboardSummary['header'], kpis: DashboardSummary['kpis'] }>(`/api/v1/dashboard/kpis${queryParams}`, { method: "GET", auth: true }),
          apiFetch<DashboardSummary['urgentAlerts']>(`/api/v1/dashboard/alerts${queryParams}`, { method: "GET", auth: true })
        ]);
        
        if (active) {
          setHeaderData(kpiRes.header);
          setKpiData(kpiRes.kpis);
          setAlertsData(alertsRes || []);
        }
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) {
          if (!redirectedRef.current) {
            redirectedRef.current = true;
            toast.error("Your session has expired. Please sign in again.");
            window.location.href = "/sign-up-login-screen";
          }
          return;
        }
        console.error("Dashboard top-level data load failed:", error);
      } finally {
        if (active) setLoadingHeader(false);
      }
    };

    loadTopLevelData();

    return () => {
      active = false;
    };
  }, [selectedDate]);

  const criticalAlerts = (alertsData || []).filter(a => a.severity === "critical");

  return (
    <AppLayout breadcrumbs={[{ label: "Dashboard" }]} userRole="administrator">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-700 text-foreground">
            Good afternoon, {user?.fullName || headerData?.greetingName || "Team"}
          </h1>

        </div>

      </div>

      <section className="mb-6">
        <KPIBentoGrid data={kpiData} loading={loadingHeader} />
      </section>

      {criticalAlerts.length > 0 && (
        <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3.5">
          <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-[13px] font-600 text-red-800">
              {criticalAlerts.length} critical alerts require immediate attention
            </p>
            <p className="text-[12px] text-red-600 mt-0.5">
              {criticalAlerts.slice(0, 2).map((a) => `${a.caseNo} (due ${a.dueDate})`).join(" - ")}
            </p>
          </div>
        </div>
      )}

      <section className="mb-6">
        <UpcomingDeadlinesTable selectedDate={selectedDate} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-1">
          <CaseDistributionChart selectedDate={selectedDate} />
        </div>
        <div className="lg:col-span-2">
          <TaskCompletionChart selectedDate={selectedDate} />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-5 mb-6">
        <UrgentAlertsPanel data={alertsData} loading={loadingHeader} />
        <RecentActivityFeed selectedDate={selectedDate} />
      </section>

    </AppLayout>
  );
}
